// App.tsx
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast, Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { 
  UserProfile, 
  ApiError,
  OnboardingFlowProps,
  LoginFlowProps,
  UserRole,
} from '@/types';
import { adminApi, ApiClientError, authApi, getApiErrorMessage } from '@/lib/api';
import { getDashboardPath, isUserRole } from '@/lib/auth';
import { clearMagicLinkParamsFromUrl, dedupeMagicLinkRequest, getMagicLinkTokenFromUrl } from '@/lib/magicLink';
import { telegramApi, getLocationFromIP } from '@/lib/telegram';

import OnboardingFlow from '@/components/OnboardingFlow';
import LoginFlow from '@/components/LoginFlow';
import RiderDashboard from '@/pages/RiderDashboard';
import DriverDashboard from '@/pages/DriverDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import UserProfilePage from '@/pages/UserProfile';
import LinkExpired from '@/pages/LinkExpired';


// Create query client with default options to prevent premature fetching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      enabled: false, // Don't run queries automatically until auth is ready
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const protectedPaths = new Set(['/driver', '/rider', '/admin', '/super-admin']);

const normalizeProfile = (user: UserProfile): UserProfile => ({
  id: user.id,
  name: user.name,
  email: user.email,
  phone_number: user.phone_number || '',
  schoolDomain: user.schoolDomain || '',
  schoolName: user.schoolName || '',
  role: (isUserRole(String(user.role).toLowerCase().trim()) ? String(user.role).toLowerCase().trim() : 'rider') as UserRole,
  avatar: user.avatar || '',
  major: user.major || 'Undeclared',
  languages: user.languages || ['English'],
  favoriteMusic: user.favoriteMusic || 'Various',
  hobbies: user.hobbies || [],
  rating: user.rating ?? 5,
  ridesCompleted: user.ridesCompleted ?? 0,
  driver_license: user.driver_license,
  license_plate: user.license_plate,
});

const enableAuthenticatedQueries = () => {
  queryClient.setDefaultOptions({
    queries: {
      enabled: true,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  });
};

const buildUserProfileFromAuth = (user: Partial<UserProfile> & { id: string; name: string; email: string; role: UserRole }): UserProfile => ({
  id: user.id,
  name: user.name,
  email: user.email,
  phone_number: user.phone_number || '',
  schoolDomain: user.schoolDomain || '',
  schoolName: user.schoolName || '',
  role: user.role,
  avatar: user.avatar || '',
  major: user.major || 'Undeclared',
  languages: user.languages || ['English'],
  favoriteMusic: user.favoriteMusic || 'Various',
  hobbies: user.hobbies || [],
  rating: user.rating ?? 5,
  ridesCompleted: user.ridesCompleted ?? 0,
  driver_license: user.driver_license,
  license_plate: user.license_plate,
});

const logMagicLinkVerificationError = (error: unknown) => {
  if (error instanceof Error) {
    console.error('[Magic Link] Verification failed:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      status: 'status' in error ? (error as { status?: number }).status : undefined,
      code: 'code' in error ? (error as { code?: string }).code : undefined,
      url: 'url' in error ? (error as { url?: string }).url : undefined,
      responseBody: 'responseBody' in error ? (error as { responseBody?: string }).responseBody : undefined,
    });
    return;
  }

  console.error('[Magic Link] Verification failed with non-Error value:', error);
};

const isExpiredMagicLinkError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  const status = error instanceof ApiClientError ? error.status : undefined;

  if (status === 410) return true;
  if (status === 400 || status === 401 || status === 404) {
    return message.includes('expired') || message.includes('invalid') || message.includes('token');
  }

  return message.includes('expired link') || message.includes('link expired');
};

const AppContent = () => {
  // Start with loading to prevent any flash of wrong content
  const [authStatus, setAuthStatus] = useState<'loading' | 'onboarding' | 'login' | 'authenticated'>('loading');
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const linkExpiredRef = useRef(false); // Track if we've already handled expired link
  
  const navigate = useNavigate();
  const location = useLocation();

  // Page time tracking using refs (to avoid re-renders)
  const pageTimesRef = useRef<{ page: string; seconds: number }[]>([]);
  const currentPageRef = useRef<string>('');
  const pageStartTimeRef = useRef<number>(Date.now());
  const lastSessionSummaryRef = useRef<string>('');

  const getRouteLabel = useCallback((pathname: string) => {
    if (pathname === '/') return 'Landing Page';
    if (pathname === '/driver') return 'Driver Dashboard';
    if (pathname === '/rider') return 'Rider Dashboard';
    if (pathname === '/admin') return 'School Admin Dashboard';
    if (pathname === '/super-admin') return 'Super Admin Dashboard';
    if (pathname === '/link-expired') return 'Expired Link';
    if (pathname.startsWith('/profile/')) return 'User Profile';
    return pathname;
  }, []);

  const upsertPageTime = useCallback((page: string, seconds: number) => {
    if (!page || seconds <= 0) return;

    const existing = pageTimesRef.current.find((entry) => entry.page === page);
    if (existing) {
      existing.seconds += seconds;
    } else {
      pageTimesRef.current.push({ page, seconds });
    }
  }, []);

  const getPageTimeSnapshot = useCallback(() => (
    pageTimesRef.current.filter((entry) => entry.seconds > 0)
  ), []);

  const getTotalTrackedTime = useCallback(() => (
    getPageTimeSnapshot().reduce((sum, entry) => sum + entry.seconds, 0)
  ), [getPageTimeSnapshot]);

  const flushCurrentPageTime = useCallback((notifyPage = false) => {
    const page = currentPageRef.current;
    if (!page) return;

    const now = Date.now();
    const seconds = Math.floor((now - pageStartTimeRef.current) / 1000);
    if (seconds <= 0) return;

    upsertPageTime(page, seconds);
    pageStartTimeRef.current = now;

    if (notifyPage) {
      telegramApi.notifyPageTimeSpent(page, seconds, getTotalTrackedTime()).catch(console.error);
    }
  }, [getTotalTrackedTime, upsertPageTime]);

  // Track page time on route change
  useEffect(() => {
    const nextPage = getRouteLabel(location.pathname);

    if (!currentPageRef.current) {
      currentPageRef.current = nextPage;
      pageStartTimeRef.current = Date.now();
      return;
    }

    if (currentPageRef.current !== nextPage) {
      flushCurrentPageTime(true);
      currentPageRef.current = nextPage;
      pageStartTimeRef.current = Date.now();
    }
  }, [flushCurrentPageTime, getRouteLabel, location.pathname]);

  // Send session summary on logout or beforeunload
  const sendSessionSummary = useCallback(async () => {
    flushCurrentPageTime(false);
    const validTimes = getPageTimeSnapshot();
    if (validTimes.length === 0) return;

    const signature = JSON.stringify(validTimes);
    if (signature === lastSessionSummaryRef.current) return;

    lastSessionSummaryRef.current = signature;
    await telegramApi.notifySessionSummary(validTimes);
  }, [flushCurrentPageTime, getPageTimeSnapshot]);

  // Send session summary on logout
  const handleLogout = useCallback(async () => {
    // Send session summary before logout
    await sendSessionSummary();
    
    localStorage.clear();
    setCurrentUser(null);
    setAuthStatus('onboarding');
    
    // Disable queries on logout
    queryClient.setDefaultOptions({
      queries: {
        enabled: false,
      },
    });
    
    queryClient.clear(); // Clear all cached data
    navigate('/', { replace: true });
    toast.success('Logged out successfully');
  }, [navigate, sendSessionSummary]);

  // Notify site visit once per browser tab/session
  useEffect(() => {
    const visitNotified = sessionStorage.getItem('telegram_visit_notified');
    if (visitNotified) return;

    getLocationFromIP()
      .then((locationText) => telegramApi.notifySiteVisit(locationText))
      .catch(console.error);

    sessionStorage.setItem('telegram_visit_notified', 'true');
  }, []);

  // Track page time on visibility changes and unload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendSessionSummary().catch(console.error);
      } else {
        pageStartTimeRef.current = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      sendSessionSummary().catch(console.error);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendSessionSummary]);

  // Quick debug: Add ?clear=true to URL to clear session
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('clear') === 'true') {
      localStorage.clear();
      window.history.replaceState({}, document.title, '/');
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      // Skip if we've already handled an expired link
      if (linkExpiredRef.current) {
        console.log('[Init] Already handled expired link, skipping auth check');
        // Still mark as initialized so the app doesn't stay in loading state
        setIsInitialized(true);
        setIsAuthReady(true);
        return;
      }
      
      // Also skip if we're already on the link-expired page
      if (location.pathname === '/link-expired') {
        console.log('[Init] Already on link-expired page, skipping auth check');
        setIsInitialized(true);
        setIsAuthReady(true);
        return;
      }
      
      console.log('[Init] Starting auth initialization...');
      console.log('[Init] Current URL snapshot:', {
        href: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      });
      
      try {
        const token = getMagicLinkTokenFromUrl();
        console.log('[Init] Magic link token present:', Boolean(token));

        // CASE 1: Magic link verification (token in URL)
        if (token) {
          if (!isMounted) return;
          setAuthStatus('loading');
          
          try {
            console.log('[Magic Link] Starting verification for token:', token.substring(0, 20) + '...');
            const response = await dedupeMagicLinkRequest(
              token,
              () => authApi.verifyMagicLink(token),
            );
            
            if (!isMounted) return;
            
            // DEBUG: Log the full response to diagnose role issue
            console.log('[DEBUG] Magic Link Response:', JSON.stringify(response, null, 2));
            console.log('[DEBUG] Response user object:', response?.user);
            console.log('[DEBUG] Response token exists:', !!response?.token);
            
            if (!response?.user?.role) {
              console.error('[DEBUG] ERROR: No user or role in response!');
              throw new Error('Invalid response: missing user role');
            }
            
            // DEBUG: Log the role before and after normalization
            const rawRole = response.user.role;
            console.log('[DEBUG] Raw role from API:', rawRole, 'Type:', typeof rawRole);
            
            const normalizedRole = String(response.user.role).toLowerCase().trim();
            console.log('[DEBUG] Normalized role:', normalizedRole);
            if (!isUserRole(normalizedRole)) {
              throw new Error(`Invalid role received: ${response.user.role}`);
            }

            const fullUserProfile = buildUserProfileFromAuth({
              ...response.user,
              role: normalizedRole,
            });

            localStorage.setItem('collegeryde_token', response.token);
            localStorage.setItem('collegeryde_user', JSON.stringify(fullUserProfile));
            
            // DEBUG: Log state before setting
            console.log('[DEBUG] Before state update - currentUser:', currentUser, 'authStatus:', authStatus);
            
            setCurrentUser(fullUserProfile);
            setAuthStatus('authenticated');
            
            // DEBUG: Log state after setting (will show old values due to closure)
            console.log('[DEBUG] After setState called - expected role:', normalizedRole);
            
            // Clean URL before navigation
            clearMagicLinkParamsFromUrl();
            
            // Enable queries now that we have auth
            enableAuthenticatedQueries();
            
            const targetPath = getDashboardPath(normalizedRole);
            console.log('[Magic Link] Verification succeeded, routing to:', targetPath);
            navigate(targetPath, { replace: true });
            
          } catch (error: unknown) {
            logMagicLinkVerificationError(error);
            if (!isMounted) return;
            
            const errorMessage = error instanceof Error ? error.message : 'Invalid or expired link';
            setIsInitialized(true);
            setIsAuthReady(true);
            clearMagicLinkParamsFromUrl();

            if (isExpiredMagicLinkError(error)) {
              console.log('[Magic Link] Redirecting to expired page because verification failed with:', errorMessage);
              linkExpiredRef.current = true;
              setAuthStatus('onboarding');
              window.history.replaceState({}, document.title, '/link-expired');
              navigate('/link-expired', { replace: true });
              return;
            }

            console.log('[Magic Link] Verification did not indicate expiration. Returning user to login with:', errorMessage);
            setAuthStatus('login');
            if (error instanceof ApiClientError && error.status && error.status >= 500) {
              toast.error('The backend failed while verifying your magic link. Please try again in a moment.');
            } else {
              toast.error(errorMessage || 'We could not verify that link. Please try again.');
            }
            navigate('/', { replace: true });
            return;
          }
        }
        // CASE 2: Existing session check (no token in URL)
        else {
          const stored = localStorage.getItem('collegeryde_user');
          const savedToken = localStorage.getItem('collegeryde_token');
          
          if (stored && savedToken) {
            try {
              const user = normalizeProfile(JSON.parse(stored) as UserProfile);
              
              if (!user?.role || !isUserRole(user.role)) {
                throw new Error('Invalid stored user data');
              }
              
              if (!isMounted) return;
              
              setCurrentUser(user);
              setAuthStatus('authenticated');
              
              // Enable queries for authenticated user
              enableAuthenticatedQueries();
              
              // Only redirect if at root
              if (location.pathname === '/') {
                const targetPath = getDashboardPath(user.role);
                navigate(targetPath, { replace: true });
              }
              
            } catch (e: unknown) {
              console.error('[Session] Failed to restore:', e);
              localStorage.clear();
              if (!isMounted) return;
              setAuthStatus('onboarding');
            }
          } else if (protectedPaths.has(location.pathname)) {
            console.log('[Session] No local token found on protected route, trying /auth/me...');

            try {
              const response = await authApi.getMe();
              if (!isMounted) return;

              const user = normalizeProfile(response.user);
              if (!user?.role || !isUserRole(user.role)) {
                throw new Error('Invalid user role returned from /auth/me');
              }

              console.log('[Session] Restored backend session via /auth/me for role:', user.role);
              setCurrentUser(user);
              setAuthStatus('authenticated');
              localStorage.setItem('collegeryde_user', JSON.stringify(user));

              enableAuthenticatedQueries();

              if (location.pathname !== getDashboardPath(user.role)) {
                navigate(getDashboardPath(user.role), { replace: true });
              }
            } catch (error) {
              console.error('[Session] /auth/me fallback failed:', error);
              if (!isMounted) return;
              setAuthStatus('onboarding');
            }
          } else {
            console.log('[Init] No session found, showing onboarding');
            if (!isMounted) return;
            
            setAuthStatus('onboarding');
          }
        }
      } catch (error) {
        console.error('[Init] Unexpected error:', error);
        if (!isMounted) return;
        setAuthStatus('onboarding');
      } finally {
        if (isMounted) {
          setIsInitialized(true);
          setIsAuthReady(true);
        }
      }
    };

    init();
    
    return () => {
      isMounted = false;
    };
  }, [navigate, location.pathname]);

  const handleUserUpdate = useCallback((updatedUser: UserProfile) => {
    setCurrentUser(updatedUser);
    localStorage.setItem('collegeryde_user', JSON.stringify(updatedUser));
  }, []);

  const handleOnboardingComplete: OnboardingFlowProps['onComplete'] = useCallback(async (
    name, 
    role, 
    email, 
    extraData
  ) => {
    try {
      await authApi.register({ 
        role, 
        name, 
        email, 
        phone_number: extraData.phone_number, 
        driver_license: extraData.driver_license, 
        license_plate: extraData.license_plate 
      });
      
      // Send Telegram notification for new registration
      telegramApi.notifyRegistration(name, email, role).catch(console.error);
      
      toast.success('Check your email for the magic link to complete login!');
      setAuthStatus('login');
    } catch (error: unknown) {
      const apiError = error as ApiError;
      if (apiError.status === 409) {
        toast.info('Account already exists! Please log in instead.');
        setAuthStatus('login');
      } else {
        toast.error(apiError.error || 'Registration failed. Please try again.');
      }
    }
  }, []);

  const handleLoginSuccess: LoginFlowProps['onLoginSuccess'] = useCallback(async (email, role) => {
    try {
      console.log('[DEBUG] Login API called with email:', email, 'role:', role);
      await authApi.login({ email, role });
      console.log('[DEBUG] Login API succeeded - magic link should be sent');
      
      // Send Telegram notification for user login
      const ipLocation = await getLocationFromIP();
      telegramApi.notifyLogin(email, ipLocation).catch(console.error);
      
      toast.success('Magic link sent! Check your email to log in.');
    } catch (error: unknown) {
      console.error('[DEBUG] Login API failed:', error);
      const apiError = error as ApiError;
      if (apiError.status === 404) {
        toast.error('No account found with this email and role. Please register first.');
      } else {
        toast.error(apiError.error || 'Failed to send login link.');
      }
    }
  }, []);

  const handleAdminLogin: LoginFlowProps['onAdminLogin'] = useCallback(async (email, password) => {
    try {
      console.log('[DEBUG] Admin login API called with email:', email, 'password supplied:', Boolean(password));
      const response = await adminApi.login({ email, password });

      const ipLocation = await getLocationFromIP();
      telegramApi.notifyLogin(email, ipLocation).catch(console.error);

      if (response.token && response.user?.role) {
        const normalizedRole = String(response.user.role).toLowerCase().trim();
        if (!isUserRole(normalizedRole)) {
          throw new Error(`Invalid role returned from admin login: ${response.user.role}`);
        }

        const fullUserProfile = buildUserProfileFromAuth({
          ...response.user,
          role: normalizedRole,
        });

        localStorage.setItem('collegeryde_token', response.token);
        localStorage.setItem('collegeryde_user', JSON.stringify(fullUserProfile));
        setCurrentUser(fullUserProfile);
        setAuthStatus('authenticated');
        enableAuthenticatedQueries();

        toast.success(normalizedRole === 'super_admin' ? 'Super admin signed in successfully.' : 'Admin signed in successfully.');
        navigate(getDashboardPath(normalizedRole), { replace: true });
        return { mode: 'direct-login' as const, message: response.message };
      }

      const successMessage = response.message || 'Admin magic link sent. Check your email to continue.';
      toast.success(successMessage);
      return { mode: 'magic-link' as const, message: successMessage };
    } catch (error: unknown) {
      if (error instanceof ApiClientError) {
        console.error('[DEBUG] Admin login API failed:', {
          status: error.status,
          message: error.message,
          url: error.url,
          responseBody: error.responseBody,
        });
      } else {
        console.error('[DEBUG] Admin login API failed:', error);
      }

      const apiError = error as ApiError;

      if (apiError.status === 403) {
        toast.error('This account does not have admin access.');
      } else if (apiError.status === 401) {
        toast.error(error instanceof ApiClientError ? error.message : 'Invalid email or password');
      } else if (apiError.status === 404) {
        toast.error('No admin account found with that email.');
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to start admin login.'));
      }

      throw error;
    }
  }, [navigate]);

  // CRITICAL: Show loading state until fully initialized
  if (!isInitialized || !isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading spinner while verifying magic link specifically
  if (authStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground animate-pulse">Verifying...</p>
        </div>
      </div>
    );
  }

  // If on link-expired page, always show LinkExpired regardless of authStatus
  if (location.pathname === '/link-expired') {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Toaster position="top-center" richColors closeButton />
        <LinkExpired />
      </div>
    );
  }

  // PWA disabled temporarily to fix React #310 crash
  // const { isInstallable } = usePWA();

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <Toaster position="top-center" richColors closeButton />
      {/* PWABanner re-enabled after auth fix */}
      
      <Routes>

        <Route 
          path="/link-expired" 
          element={<LinkExpired />} 
        />
        
        <Route 
          path="/" 
          element={
            authStatus === 'authenticated' && currentUser ? (
              <Navigate to={getDashboardPath(currentUser.role)} replace />
            ) : authStatus === 'login' ? (
              <LoginFlow
                onLoginSuccess={handleLoginSuccess}
                onAdminLogin={handleAdminLogin}
                onRegisterClick={() => setAuthStatus('onboarding')}
              />
            ) : (
              <OnboardingFlow
                onComplete={handleOnboardingComplete}
                onLoginClick={() => setAuthStatus('login')}
              />
            )
          } 
        />

        
        <Route 
          path="/rider" 
          element={
            (console.log('[DEBUG] /rider route check - authStatus:', authStatus, 'role:', currentUser?.role),
            authStatus === 'authenticated' && currentUser?.role === 'rider') ? (
              <RiderDashboard currentUser={currentUser} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route 
          path="/driver" 
          element={
            (console.log('[DEBUG] /driver route check - authStatus:', authStatus, 'role:', currentUser?.role),
            authStatus === 'authenticated' && currentUser?.role === 'driver') ? (
              <DriverDashboard currentUser={currentUser} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />

        <Route
          path="/admin"
          element={
            authStatus === 'authenticated' && currentUser?.role === 'admin' ? (
              <AdminDashboard currentUser={currentUser} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/super-admin"
          element={
            authStatus === 'authenticated' && currentUser?.role === 'super_admin' ? (
              <SuperAdminDashboard currentUser={currentUser} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        
        <Route 
          path="/profile/:userId" 
          element={
            authStatus === 'authenticated' && currentUser ? (
              <UserProfilePage currentUser={currentUser} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};


const App = () => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  </BrowserRouter>
);

export default App;
